import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { fmtError } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Users, Heart, Zap, Loader2, Compass, Mail, Lock, User, Calendar, LogIn, UserPlus } from "lucide-react";
import { InstallButton } from "@/components/app/InstallPrompt";
import { SignIn, useAuth as useClerkAuth } from "@clerk/react";

const VIBES = [
  { color: "linear-gradient(135deg, #FFE6E0, #FFCFC4)", icon: Heart, labelKey: "authHero.makeFriends" },
  { color: "linear-gradient(135deg, #DDF5EA, #BFE9D6)", icon: Users, labelKey: "authHero.findSquads" },
  { color: "linear-gradient(135deg, #ECE6FF, #D9CFFF)", icon: Sparkles, labelKey: "authHero.tryNew" },
  { color: "linear-gradient(135deg, #FFF1D6, #FFE08A)", icon: Zap, labelKey: "authHero.live" },
];

export default function Auth() {
  const { refresh, loginAsGuest, login, register } = useAuth();
  const nav = useNavigate();
  const { t } = useTranslation();
  const [clerkBusy, setClerkBusy] = useState(false);
  const [guestLoading, setGuestLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { isLoaded, isSignedIn, getToken } = useClerkAuth();

  // Detect if running inside mobile WebView / Capacitor
  const isMobileEnvironment = typeof window !== "undefined" && (
    Boolean(window.Capacitor) ||
    window.location.protocol === "capacitor:" ||
    window.location.protocol === "file:" ||
    navigator.userAgent.includes("; wv") ||
    navigator.userAgent.includes("Android")
  );

  const [authMethod, setAuthMethod] = useState(isMobileEnvironment ? "email" : "clerk");
  const [emailTab, setEmailTab] = useState("signin"); // "signin" | "signup"

  // Form states
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [age, setAge] = useState(20);
  const [gender, setGender] = useState("other");

  const handleGuestLogin = async () => {
    setGuestLoading(true);
    try {
      const u = await loginAsGuest();
      if (u) {
        toast.success("Welcome! Browsing TANTA as a Guest 🚀");
        nav("/feed");
      }
    } catch (err) {
      toast.error("Could not sign in as guest. Please try again.");
    } finally {
      setGuestLoading(false);
    }
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please enter email and password");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setSubmitting(true);
    try {
      if (emailTab === "signin") {
        const u = await login(email, password);
        if (u) {
          toast.success("Welcome back to TANTA! 🎉");
          nav("/feed");
        }
      } else {
        if (!name.trim()) {
          toast.error("Please enter your name");
          setSubmitting(false);
          return;
        }
        const u = await register({
          email,
          password,
          name: name.trim(),
          age: parseInt(age, 10) || 18,
          gender,
        });
        if (u) {
          toast.success("Account created! Welcome to TANTA! 🎉");
          nav("/feed");
        }
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || fmtError(err) || "Authentication failed. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Authentication failed");
    } finally {
      setSubmitting(false);
    }
  };

  // When Clerk signs in, wait for AuthContext to fetch the TANTA profile and redirect
  useEffect(() => {
    if (!isLoaded || !isSignedIn || clerkBusy) return;
    const completeSignIn = async () => {
      setClerkBusy(true);
      try {
        let token = null;
        for (let i = 0; i < 20 && !token; i++) {
          try {
            if (typeof getToken === "function") {
              token = await getToken();
            }
            if (!token && window.Clerk) {
              if (window.Clerk.session) {
                token = await window.Clerk.session.getToken();
              } else if (typeof window.Clerk.getToken === "function") {
                token = await window.Clerk.getToken();
              }
            }
          } catch (_) { /* not ready */ }
          if (!token) await new Promise((r) => setTimeout(r, 200));
        }

        let u = null;
        for (let attempt = 0; attempt < 3 && !u; attempt++) {
          if (attempt > 0) await new Promise((r) => setTimeout(r, 600 * attempt));
          u = await refresh();
        }

        if (u) {
          toast.success("Welcome to TANTA! 🎉");
          window.location.href = "/feed";
        } else {
          toast.error("Could not sync account profile. Please try refreshing.");
          setClerkBusy(false);
        }
      } catch (err) {
        toast.error("Sign-in failed, please try again");
        setClerkBusy(false);
      }
    };
    completeSignIn();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, isSignedIn]);

  return (
    <div className="auth-page-root min-h-screen relative overflow-hidden">
      {/* ── Animated mesh-gradient background ── */}
      <div className="auth-bg-gradient" aria-hidden="true" />
      <div className="auth-bg-orb orb-1" aria-hidden="true" />
      <div className="auth-bg-orb orb-2" aria-hidden="true" />
      <div className="auth-bg-orb orb-3" aria-hidden="true" />

      {/* ── Content ── */}
      <div className="relative z-10 min-h-screen p-5 sm:p-8 flex flex-col lg:flex-row items-stretch gap-6 max-w-7xl mx-auto">

        {/* Left vibe panel */}
        <div className="flex-1 flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <img
                src={"https://customer-assets." + atob("ZW1lcmdlbnRhZ2VudC5jb20=") + "/job_activity-squad-2/artifacts/4cw1jeaq_1a81f51e-4e78-47f6-81c3-cfddeb7a76dc.png"}
                alt="TANTA"
                className="w-14 h-14 object-contain animate-float"
                draggable={false}
              />
              <span className="font-display font-extrabold text-2xl tracking-[0.2em] bg-gradient-to-r from-[#0F9D8F] via-[#14C8B0] to-[#5EEAD4] bg-clip-text text-transparent">TANTA</span>
            </div>
            <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight mb-4">
              {t("authHero.real1")}<br />{t("authHero.real2")}<br />
              <span className="font-script font-normal bg-gradient-to-r from-[#14C8B0] to-[#5EEAD4] bg-clip-text text-transparent">
                {t("authHero.tagline")}
              </span>
            </h1>
            <p className="text-base sm:text-lg text-[#4A4F58] max-w-md leading-relaxed">
              {t("authHero.body")}
            </p>
            <div className="mt-5">
              <InstallButton />
            </div>
          </div>
          <div className="hidden lg:grid grid-cols-2 gap-4 mt-10 max-w-md">
            {VIBES.map((v) => (
              <div key={v.labelKey} className="card-nb p-5 flex items-center gap-3" style={{ background: v.color }}>
                <v.icon className="w-7 h-7" strokeWidth={2.5} />
                <span className="font-display font-bold">{t(v.labelKey)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right form card */}
        <div className="flex-1 flex flex-col items-center justify-center gap-4">
          {authMethod === "email" ? (
            <div className="shadow-nb border-2 border-[#0A0B0D] rounded-3xl overflow-hidden bg-white/95 backdrop-blur-xl w-full max-w-md p-6 sm:p-8">
              {/* Tab Switcher */}
              <div className="flex rounded-xl border-2 border-[#0A0B0D] bg-[#F4F4F0] p-1 mb-6">
                <button
                  type="button"
                  onClick={() => setEmailTab("signin")}
                  className={`flex-1 py-2 rounded-lg font-display font-bold text-sm transition-all flex items-center justify-center gap-2 ${
                    emailTab === "signin"
                      ? "bg-[#14C8B0] text-[#0A0B0D] shadow-sm border border-[#0A0B0D]"
                      : "text-[#7C828D] hover:text-[#0A0B0D]"
                  }`}
                >
                  <LogIn className="w-4 h-4" />
                  <span>Sign In</span>
                </button>
                <button
                  type="button"
                  onClick={() => setEmailTab("signup")}
                  className={`flex-1 py-2 rounded-lg font-display font-bold text-sm transition-all flex items-center justify-center gap-2 ${
                    emailTab === "signup"
                      ? "bg-[#14C8B0] text-[#0A0B0D] shadow-sm border border-[#0A0B0D]"
                      : "text-[#7C828D] hover:text-[#0A0B0D]"
                  }`}
                >
                  <UserPlus className="w-4 h-4" />
                  <span>Sign Up</span>
                </button>
              </div>

              <div className="mb-5">
                <h2 className="font-display font-black text-2xl text-[#0A0B0D] tracking-tight">
                  {emailTab === "signin" ? "Welcome back!" : "Create account"}
                </h2>
                <p className="text-xs text-[#7C828D] font-bold mt-1">
                  {emailTab === "signin"
                    ? "Sign in with your email & password"
                    : "Join TANTA to meet real friends near you"}
                </p>
              </div>

              <form onSubmit={handleEmailAuth} className="space-y-3.5">
                {emailTab === "signup" && (
                  <>
                    <div>
                      <label className="block font-bold text-xs text-[#0A0B0D] mb-1">Full Name</label>
                      <div className="relative">
                        <User className="w-4 h-4 text-[#7C828D] absolute left-3.5 top-1/2 -translate-y-1/2" />
                        <input
                          type="text"
                          required
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder="Your name"
                          className="w-full pl-10 pr-3.5 h-11 rounded-xl border-2 border-[#0A0B0D] bg-white text-sm font-medium focus:border-[#14C8B0] outline-none transition-colors"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2.5">
                      <div>
                        <label className="block font-bold text-xs text-[#0A0B0D] mb-1">Age</label>
                        <div className="relative">
                          <Calendar className="w-4 h-4 text-[#7C828D] absolute left-3.5 top-1/2 -translate-y-1/2" />
                          <input
                            type="number"
                            min="13"
                            max="100"
                            required
                            value={age}
                            onChange={(e) => setAge(e.target.value)}
                            className="w-full pl-10 pr-3.5 h-11 rounded-xl border-2 border-[#0A0B0D] bg-white text-sm font-medium focus:border-[#14C8B0] outline-none transition-colors"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block font-bold text-xs text-[#0A0B0D] mb-1">Gender</label>
                        <select
                          value={gender}
                          onChange={(e) => setGender(e.target.value)}
                          className="w-full px-3 h-11 rounded-xl border-2 border-[#0A0B0D] bg-white text-sm font-medium focus:border-[#14C8B0] outline-none transition-colors"
                        >
                          <option value="female">Female</option>
                          <option value="male">Male</option>
                          <option value="other">Other / Non-binary</option>
                        </select>
                      </div>
                    </div>
                  </>
                )}

                <div>
                  <label className="block font-bold text-xs text-[#0A0B0D] mb-1">Email Address</label>
                  <div className="relative">
                    <Mail className="w-4 h-4 text-[#7C828D] absolute left-3.5 top-1/2 -translate-y-1/2" />
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="w-full pl-10 pr-3.5 h-11 rounded-xl border-2 border-[#0A0B0D] bg-white text-sm font-medium focus:border-[#14C8B0] outline-none transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="block font-bold text-xs text-[#0A0B0D] mb-1">Password</label>
                  <div className="relative">
                    <Lock className="w-4 h-4 text-[#7C828D] absolute left-3.5 top-1/2 -translate-y-1/2" />
                    <input
                      type="password"
                      required
                      minLength={6}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="w-full pl-10 pr-3.5 h-11 rounded-xl border-2 border-[#0A0B0D] bg-white text-sm font-medium focus:border-[#14C8B0] outline-none transition-colors"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full mt-2 rounded-full border-2 border-[#0A0B0D] shadow-nb-sm hover:translate-y-[1px] active:translate-y-[2px] transition-all bg-[#14C8B0] hover:bg-[#0F9D8F] text-[#0A0B0D] font-display font-extrabold text-base h-12 flex items-center justify-center gap-2 cursor-pointer"
                >
                  {submitting ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : emailTab === "signin" ? (
                    <span>Sign In</span>
                  ) : (
                    <span>Create Account</span>
                  )}
                </button>
              </form>

              {!isMobileEnvironment && (
                <div className="mt-4 pt-3 border-t border-[#E2DFD4] text-center">
                  <button
                    type="button"
                    onClick={() => setAuthMethod("clerk")}
                    className="text-xs text-[#14C8B0] hover:text-[#0F9D8F] font-bold"
                  >
                    Switch to Google / Clerk Sign In →
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <SignIn
                routing="hash"
                appearance={{
                  variables: {
                    colorPrimary: '#14C8B0',
                    colorTextOnPrimaryBackground: '#0A0B0D',
                    fontFamily: 'Outfit, sans-serif',
                    borderRadius: '1.25rem',
                  },
                  elements: {
                    cardBox: 'shadow-nb border-2 border-[#0A0B0D] rounded-3xl overflow-hidden bg-white/95 backdrop-blur-xl w-full max-w-md',
                    card: 'bg-transparent shadow-none border-none p-6 sm:p-8 w-full',
                    headerTitle: 'font-display font-black text-3xl text-[#0A0B0D] tracking-tight',
                    headerSubtitle: 'text-sm text-[#7C828D] font-bold mt-1.5',
                    socialButtonsBlockButton: 'rounded-full border-2 border-[#0A0B0D] shadow-nb-sm hover:translate-y-[1px] active:translate-y-[2px] transition-all bg-white hover:bg-[#F7F7F2] h-12 text-sm font-bold text-[#0A0B0D]',
                    formButtonPrimary: 'rounded-full border-2 border-[#0A0B0D] shadow-nb-sm hover:translate-y-[1px] active:translate-y-[2px] transition-all bg-[#14C8B0] hover:bg-[#0F9D8F] text-[#0A0B0D] font-display font-extrabold text-base h-12',
                    formFieldInput: 'rounded-xl border-2 border-[#0A0B0D] bg-white focus:border-[#14C8B0] focus:ring-0 transition-colors h-11 text-sm',
                    formFieldLabel: 'font-bold text-xs text-[#0A0B0D]',
                    footer: 'bg-transparent pt-4',
                    footerActionText: 'text-xs text-[#7C828D] font-bold',
                    footerActionLink: 'text-xs text-[#14C8B0] hover:text-[#0F9D8F] font-bold',
                    dividerText: 'text-xs text-[#7C828D] font-bold',
                    dividerLine: 'bg-[#E2DFD4]',
                  }
                }}
              />
              <button
                type="button"
                onClick={() => setAuthMethod("email")}
                className="text-xs text-[#4A4F58] hover:text-[#0A0B0D] font-bold underline cursor-pointer"
              >
                Having trouble? Sign in with Email & Password
              </button>
            </>
          )}

          <button
            type="button"
            onClick={handleGuestLogin}
            disabled={guestLoading}
            className="w-full max-w-md py-3.5 px-6 rounded-2xl border-2 border-[#0A0B0D] bg-white hover:bg-[#F7F7F2] shadow-nb font-display font-extrabold text-sm text-[#0A0B0D] flex items-center justify-center gap-2.5 transition-all hover:translate-y-[-1px] active:translate-y-[1px] cursor-pointer"
          >
            {guestLoading ? (
              <Loader2 className="w-5 h-5 animate-spin text-[#14C8B0]" />
            ) : (
              <Compass className="w-5 h-5 text-[#14C8B0]" />
            )}
            <span>{t("auth.continueAsGuest", "Continue as Guest")}</span>
          </button>
        </div>
      </div>

      {clerkBusy && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm">
          <div className="bg-white rounded-2xl px-8 py-6 flex items-center gap-3 shadow-xl">
            <Loader2 className="w-5 h-5 animate-spin text-[#14C8B0]" />
            <span className="font-display font-bold">Signing you in…</span>
          </div>
        </div>
      )}
    </div>
  );
}
